const WheelAlignment = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/installation.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Why Wheel Alignment Matters & How Often You Should Do It
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Proper wheel alignment keeps your vehicle safe, smooth, and efficient. Here’s why it’s essential — and when you should get it checked.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Many drivers overlook wheel alignment until steering feels off or their tires start to wear unevenly. But alignment affects more than just straight-line driving — it impacts your tire life, fuel efficiency, handling, and overall safety.
            At <strong>Express Tire Works</strong>, we believe staying informed is the best way to keep your vehicle running smoothly. Here’s everything you need to know about wheel alignment and how often you should schedule it.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. What Is Wheel Alignment?
            </h2>
            <p>
              Wheel alignment is the adjustment of your vehicle’s suspension — the system that connects the wheels to the car. It ensures your tires meet the road at the correct angles for optimal performance.
            </p>
            <p className="mt-3">Alignment focuses on three key angles:</p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Camber:</strong> Inward or outward tilt of the tire when viewed from the front.</li>
              <li><strong>Caster:</strong> The angle of the steering axis that helps with stability.</li>
              <li><strong>Toe:</strong> The direction the tires point inward or outward when viewed from above.</li>
            </ul>
            <p>
              When these angles shift out of balance, your vehicle becomes less stable, and your tire wear accelerates.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Why Proper Wheel Alignment Matters
            </h2>
            <p>
              Wheel alignment directly affects the lifespan and performance of your tires. But its benefits extend far beyond tread wear:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Improved fuel efficiency:</strong> Misaligned wheels increase rolling resistance, forcing your engine to work harder.</li>
              <li><strong>Smoother handling:</strong> Proper alignment ensures your car drives straight and responds correctly to steering input.</li>
              <li><strong>Longer tire life:</strong> Prevents uneven tread wear and premature replacement.</li>
              <li><strong>Enhanced safety:</strong> Better traction, stability, and control — especially during braking.</li>
              <li><strong>Reduced strain on suspension:</strong> Protects components like shocks, struts, and steering systems.</li>
            </ul>
            <p>
              Overall, proper alignment saves you money and keeps you safer on the road.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Signs Your Vehicle Needs Wheel Alignment
            </h2>
            <p>
              You don’t have to wait until your next service appointment — your vehicle may already be showing warning signs.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Your car pulls to one side while driving</li>
              <li>Uneven or rapid tire wear</li>
              <li>Steering wheel appears crooked even when driving straight</li>
              <li>Vibration in the steering wheel</li>
              <li>Tires squeal or make unusual noises</li>
            </ul>
            <p>
              If you notice any of these symptoms, it’s time for a professional alignment check.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. How Often Should You Get a Wheel Alignment?
            </h2>
            <p>
              As a general guideline, most vehicles should get a wheel alignment:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Every 6,000 to 8,000 miles</strong> (around every 6 months)</li>
              <li><strong>After replacing tires</strong></li>
              <li><strong>After hitting potholes or curbs</strong></li>
              <li><strong>If suspension parts are repaired or replaced</strong></li>
              <li><strong>If you notice alignment-related symptoms</strong></li>
            </ul>
            <p>
              Your driving environment plays a role too — rough roads, potholes, and speed bumps can knock your alignment out of balance quickly.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. What Happens During a Professional Alignment?
            </h2>
            <p>
              At <strong>Express Tire Works</strong>, we use advanced alignment machines that precisely measure your wheel angles. A typical alignment includes:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Inspection of suspension and steering components</li>
              <li>Adjustment of camber, caster, and toe angles</li>
              <li>Tire pressure check</li>
              <li>Road test to ensure proper handling</li>
            </ul>
            <p>
              This ensures your vehicle drives straight, smooth, and safe.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. The Benefits of Regular Alignment Service
            </h2>
            <p>Your car will experience several long-term advantages, including:</p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Extended tire lifespan</li>
              <li>Better fuel economy</li>
              <li>Improved steering and handling</li>
              <li>Reduced maintenance costs</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Wheel alignment is a simple, affordable service that prevents major problems down the road. By keeping your wheels properly aligned, you ensure safer driving, a smoother ride, and longer-lasting tires.
              For expert alignment services, book an appointment with <strong>Express Tire Works</strong> — where performance, precision, and customer care come first.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Wheel Alignment</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Suspension</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Care</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Maintenance Tips</p>
        </div>
      </section>
    </div>
  );
};

export default WheelAlignment;